import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  Code, 
  Database, 
  Smartphone, 
  Cloud, 
  Users, 
  Briefcase, 
  Calendar, 
  FileText, 
  Package, 
  Brain, 
  Shield, 
  Cpu, 
  Scale, 
  Cog,
  Link,
  Leaf,
  TestTube,
  Bot,
  Boxes,
  LineChart
} from "lucide-react";
import { useContext } from "react";
import { CalendlyContext } from "@/App";

const services = [
  {
    title: "SaaS Development",
    description: "End-to-end SaaS solutions with scalable architecture and subscription management.",
    icon: <Package className="h-10 w-10 text-emerald-500" />,
    features: ["Multi-tenant Architecture", "Subscription Management", "Usage Analytics", "API Integration"]
  },
  {
    title: "Adaptive AI Systems",
    description: "Customized AI for industry-specific challenges with transparent, auditable models.",
    icon: <Database className="h-10 w-10 text-emerald-500" />,
    features: ["Vertical AI Solutions", "Autonomous Workflows", "Explainable AI", "Continuous Learning"]
  },
  {
    title: "Generative AI Solutions",
    description: "Transform your business with cutting-edge generative AI capabilities.",
    icon: <Brain className="h-10 w-10 text-emerald-500" />,
    features: ["Custom LLM Development", "Content Generation", "Creative AI Tools", "AI Integration"]
  },
  {
    title: "AI Security & Cybersecurity",
    description: "Advanced AI-powered security solutions for modern threats.",
    icon: <Shield className="h-10 w-10 text-emerald-500" />,
    features: ["Threat Detection", "Security Automation", "Vulnerability Assessment", "Compliance Monitoring"]
  },
  {
    title: "Edge AI & IoT Solutions",
    description: "Intelligent edge computing and IoT solutions for real-time processing.",
    icon: <Cpu className="h-10 w-10 text-emerald-500" />,
    features: ["Edge AI Deployment", "IoT Device Management", "Real-time Processing", "Edge-to-Cloud Integration"]
  },
  {
    title: "AI Ethics & Governance",
    description: "Ensure responsible and ethical AI implementation.",
    icon: <Scale className="h-10 w-10 text-emerald-500" />,
    features: ["Ethics Consulting", "Responsible AI", "Governance Frameworks", "Bias Mitigation"]
  },
  {
    title: "AI Model Training & MLOps",
    description: "End-to-end AI model development and deployment services.",
    icon: <Cog className="h-10 w-10 text-emerald-500" />,
    features: ["Custom Model Training", "MLOps Implementation", "Model Deployment", "Data Pipeline Automation"]
  },
  {
    title: "AI-Driven Transformation",
    description: "Revolutionize operations with AI-powered automation and analytics.",
    icon: <Chart className="h-10 w-10 text-emerald-500" />,
    features: ["Process Automation", "Customer Engagement", "Risk Management", "Strategic Planning"]
  },
  {
    title: "Cloud Development",
    description: "Scalable cloud solutions built on AWS, Azure, and Google Cloud platforms.",
    icon: <Cloud className="h-10 w-10 text-emerald-500" />,
    features: ["Microservices", "Serverless Architecture", "Cloud Migration", "Multi-Cloud Strategy"]
  },
  {
    title: "DevOps",
    description: "Accelerate delivery with CI/CD pipelines and infrastructure as code.",
    icon: <Code className="h-10 w-10 text-emerald-500" />,
    features: ["CI/CD Pipelines", "Infrastructure as Code", "Monitoring", "Containerization"]
  },
  {
    title: "Mobile Development",
    description: "Responsive iOS and Android apps with seamless user experience.",
    icon: <Smartphone className="h-10 w-10 text-emerald-500" />,
    features: ["Native Apps", "Cross-Platform", "UX/UI Design", "Mobile Strategy"]
  },
  {
    title: "Web Development",
    description: "High-performance websites and web applications with modern frameworks.",
    icon: <Code className="h-10 w-10 text-emerald-500" />,
    features: ["Responsive Design", "Progressive Web Apps", "E-commerce", "Web Portals"]
  },
  {
    title: "IT Consulting",
    description: "Strategic guidance for digital transformation initiatives.",
    icon: <Briefcase className="h-10 w-10 text-emerald-500" />,
    features: ["Digital Strategy", "Technology Assessment", "IT Roadmap", "Business Analysis"]
  },
  {
    title: "Digital Marketing",
    description: "SEO, content marketing, and AI-driven ad campaigns.",
    icon: <Users className="h-10 w-10 text-emerald-500" />,
    features: ["SEO", "Content Strategy", "PPC Campaigns", "Analytics & Reporting"]
  },
  {
    title: "Blockchain & Web3 Development",
    description: "End-to-end blockchain solutions and Web3 applications for decentralized ecosystems.",
    icon: <BlockchainIcon className="h-10 w-10 text-emerald-500" />,
    features: ["Smart Contract Development", "dApp Development", "NFT Solutions", "DeFi Integration"]
  },
  {
    title: "Extended Reality (XR) Development",
    description: "Immersive AR/VR solutions for training, visualization, and interactive experiences.",
    icon: <VRGlassesIcon className="h-10 w-10 text-emerald-500" />,
    features: ["AR Applications", "VR Experiences", "3D Modeling", "Immersive Training"]
  },
  {
    title: "Robotic Process Automation",
    description: "Automate repetitive tasks and streamline business processes with intelligent bots.",
    icon: <Bot className="h-10 w-10 text-emerald-500" />,
    features: ["Process Automation", "Workflow Optimization", "Bot Development", "RPA Integration"]
  },
  {
    title: "Quality Engineering & Testing",
    description: "Comprehensive testing and quality assurance services for robust applications.",
    icon: <TestTube className="h-10 w-10 text-emerald-500" />,
    features: ["Test Automation", "Performance Testing", "Security Testing", "QA Consulting"]
  },
  {
    title: "API & Integration Services",
    description: "Seamless integration solutions and API development for connected ecosystems.",
    icon: <Link className="h-10 w-10 text-emerald-500" />,
    features: ["API Development", "Integration Architecture", "API Management", "Third-party Integration"]
  },
  {
    title: "Sustainability Tech Solutions",
    description: "Green technology solutions and ESG-focused software development.",
    icon: <Leaf className="h-10 w-10 text-emerald-500" />,
    features: ["ESG Reporting", "Carbon Tracking", "Green IT Solutions", "Sustainability Analytics"]
  },
  {
    title: "Data Engineering & Analytics",
    description: "Advanced data solutions for business intelligence and analytics.",
    icon: <LineChart className="h-10 w-10 text-emerald-500" />,
    features: ["Data Warehousing", "ETL Pipelines", "Business Intelligence", "Data Visualization"]
  },
  {
    title: "Low-Code/No-Code Development",
    description: "Rapid application development using low-code and no-code platforms.",
    icon: <Boxes className="h-10 w-10 text-emerald-500" />,
    features: ["Platform Development", "Workflow Automation", "Citizen Development", "Integration Services"]
  }
];

// Custom Chart icon component
function Chart(props: any) {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      {...props}
    >
      <path d="M3 3v18h18"></path>
      <path d="M7 16l4-8 4 4 4-10"></path>
    </svg>
  );
}

// Custom Blockchain icon component
function BlockchainIcon(props: any) {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      {...props}
    >
      <circle cx="12" cy="12" r="10"></circle>
      <path d="M8 12h8"></path>
      <path d="M12 8v8"></path>
    </svg>
  );
}

// Custom VR Glasses icon component
function VRGlassesIcon(props: any) {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      {...props}
    >
      <path d="M2 12h20"></path>
      <path d="M12 2v20"></path>
      <circle cx="12" cy="12" r="10"></circle>
    </svg>
  );
}

const ServicesSection = () => {
  const { setIsOpen } = useContext(CalendlyContext);
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-16 md:py-24 bg-gradient-to-br from-emerald-700 to-emerald-900 text-white overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold font-montserrat mb-4">
            Our Services
          </h2>
          <p className="text-xl opacity-90 max-w-3xl mx-auto">
            Comprehensive solutions to transform your business with AI and premium software.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <Card 
              key={index} 
              className="service-card hover-card overflow-hidden group transition-all duration-300"
            >
              <CardContent className="p-6">
                <div className="mb-6">
                  {service.icon}
                </div>
                <h3 className="text-xl font-montserrat font-semibold mb-3 group-hover:text-emerald-600 transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {service.description}
                </p>
                <ul className="mb-4 space-y-2">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-2"></div>
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  variant="link" 
                  className="text-emerald-600 p-0 hover:text-emerald-700 group-hover:translate-x-1 transition-transform duration-300"
                >
                  Learn More <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="text-center mt-12">
          <Button 
            className="bg-white text-emerald-700 hover:bg-gold-500 hover:text-gray-800 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg text-lg py-6 px-8 rounded-md"
            onClick={() => setIsOpen(true)}
          >
            <Briefcase className="mr-2 h-5 w-5" />
            Get a Free Consultation
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
